import { IBlogItemOverview } from 'common/types/blog';
import { IAnyType, IServerProps } from 'common/types/common';
import { Article } from 'common/types/content/graphql/graphql';
import { buildAbsoluteUrl } from 'common/utils/url';
import { createApolloClient } from 'core/clientContent';
import { ENV_VARIABLES } from 'core/config';
import { QUERY_CONTENT_POSTS_MINIMAL } from 'core/query/posts';
import { Layout } from 'layout';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const HeroBlogList = dynamic(() =>
  import('modules/blog/HeroBlogList').then(mod => mod.HeroBlogList),
);
const BlogList = dynamic(() => import('modules/blog/BlogList').then(mod => mod.BlogList));
const CTA = dynamic(() => import('common/components/Cta').then(mod => mod.CTA));

const BlogPage = (pageProps: IBlogPageProps) => {
  const { t } = useTranslation('meta');

  const blogsAll = t('blogItemsOverview:blogItems', { returnObjects: true }) as IBlogItemOverview[];
  const blogsActive = blogsAll.filter(blog => blog.active);
  const base = t('common:company.url');
  const publisher = {
    '@type': 'Organization',
    name: 'Nordit d.o.o.',
    url: 'https://nordit.co',
    logo: {
      '@type': 'ImageObject',
      url: 'https://firebasestorage.googleapis.com/v0/b/nordit-83a0b.appspot.com/o/logo%2FNordit%20logo%20-%20primary%20white%2FNordit%20logo%20-%20primary%20white%402x.png?alt=media&token=d979ae78-414f-4fd1-bfb5-dc31ad8ce3cf',
    },
  };
  const schemaMarkup: IAnyType[] = [];
  if (pageProps.data.contentArticles.length) {
    pageProps.data.contentArticles.forEach((article: Article) => {
      const url = article.slug ? `${base}/blog/${article.slug}` : '';
      schemaMarkup.push({
        '@type': 'BlogPosting',
        mainEntityOfPage: { '@type': 'WebPage', '@id': base },
        headline: article.seo?.title || article.title,
        image: article.seo?.image?.url || article.cover?.url || '',
        description: article.seo?.description || article.description,
        url,
        publisher,
      });
    });
  }
  if (blogsActive.length) {
    blogsActive.forEach(blog => {
      const url = blog.slug ? buildAbsoluteUrl(base, blog.slug) : '';
      schemaMarkup.push({
        '@type': 'BlogPosting',
        mainEntityOfPage: { '@type': 'WebPage', '@id': base },
        headline: blog.seo.title,
        image: blog.seo.image,
        description: blog.seo.description,
        author: {
          '@type': 'Person',
          name: blog.author.name,
          url: 'https://www.linkedin.com/in/vukusicivan/',
        },
        url,
        publisher,
        datePublished: blog.date,
      });
    });
  }

  const seo = {
    alternate: t('blogList.alternate', { returnObjects: true }),
    description: t('blogList.description'),
    image: t('blogList.image'),
    schemaMarkup,
    slug: t('blogList.slug'),
    title: t('blogList.title'),
  };
  return (
    <Layout dark seo={seo}>
      <HeroBlogList />
      <BlogList contentArticles={pageProps.data.contentArticles} />
      <CTA dark fullHeight={false} />
    </Layout>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<IServerProps<IBlogPageProps>> => {
  const locale = await serverSideTranslations(context.locale || 'en', [
    'blogItemsOverview',
    'common',
    'meta',
    'pageBlogList',
  ]);
  const publicationState = ENV_VARIABLES.APP_ENV !== 'production' ? 'DRAFT' : 'PUBLISHED';
  let contentArticles: Article[] = [];
  try {
    const clientContent = createApolloClient();
    const { data: dataPosts } = await clientContent.query({
      query: QUERY_CONTENT_POSTS_MINIMAL,
      variables: {
        locale: context.locale || 'en',
        status: publicationState,
        sort: ['date:desc'],
      },
    });
    const posts = dataPosts as { articles_connection: { nodes: Article[] } };
    contentArticles = posts.articles_connection.nodes.map((article: Article) => article);
  } catch (error) {
    console.error('Error fetching posts:', error);
  }

  return {
    props: {
      data: {
        contentArticles,
        referer: context.req.headers.referer || null,
      },
      ...locale,
    },
  };
};

interface IBlogPageProps {
  data: { contentArticles: Article[]; referer?: string | null } | Record<string, never>;
}

export default BlogPage;
